import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, type FieldPath } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  canonicalSportName,
  SPORT_CATALOGUE,
  submitTripRequestSchema,
  type BoardBasis,
  type CreateTripInput,
  type ExtraService,
  type SubmitTripRequestInput,
  type TripDto,
  type TripStatus,
} from "@tc/shared";
import {
  Button,
  Card,
  CheckCard,
  CountrySelect,
  DateRangePicker,
  Field,
  Icon,
  Select,
  Stepper,
  Textarea,
  TextInput,
  type IconName,
} from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { useLanguage } from "@/lib/i18n/language";
import { ApiError, tripsApi } from "./api";

/**
 * The hero club flow (TW-3438): a multi-step "new trip request" wizard. A club user fills
 * match/team details, transport prefs, baggage, rooms, board and extras, reviews, and submits →
 * a Trip is created in `Enquiry` (the TW-3437 POST /api/trips) and TC is notified. Validation is
 * the shared `submitTripRequestSchema` (one rule with the API). Ported from the prototype
 * (`design/prototype/project/app/request.jsx`) onto the Ink & Gold tokens + `components/ui`.
 */

// Fixed sport catalogue (shared) — the reference generator maps these names to short codes
// (Hockey→HK, …); the empty choice means "no sport" (reference falls back to GEN).
const SPORT_OPTIONS = SPORT_CATALOGUE.map((s) => ({ value: s.name, label: s.name }));

// Players/Staff are picked from a 0–50 dropdown rather than typed.
const PAX_OPTIONS = Array.from({ length: 51 }, (_, i) => ({ value: String(i), label: String(i) }));

const TRANSPORT_MODES = [
  { key: "flight", icon: "flight", label: "request.fFlight" },
  { key: "train", icon: "train", label: "request.fTrain" },
  { key: "coach", icon: "coach", label: "request.fCoach" },
  { key: "hireCar", icon: "car", label: "request.fHireCar" },
] as const;

const BOARD_OPTS: { value: BoardBasis; label: string }[] = [
  { value: "RO", label: "request.boardRo" },
  { value: "BB", label: "request.boardBb" },
  { value: "HB", label: "request.boardHb" },
  { value: "FB", label: "request.boardFb" },
];

const ROOM_KEYS = [
  { key: "single", label: "request.fSingle" },
  { key: "double", label: "request.fDouble" },
  { key: "twin", label: "request.fTwin" },
  { key: "triple", label: "request.fTriple" },
] as const;

const EXTRA_OPTS: { key: ExtraService; icon: IconName; label: string }[] = [
  { key: "meetingRoom", icon: "building", label: "request.xMeeting" },
  { key: "projector", icon: "doc", label: "request.xProjector" },
  { key: "massageTable", icon: "bed", label: "request.xMassage" },
  { key: "iceRecovery", icon: "whistle", label: "request.xIce" },
  { key: "laundry", icon: "bag", label: "request.xLaundry" },
  { key: "meals", icon: "utensils", label: "request.xMeals" },
];

const STEPS = ["match", "travel", "rooms", "extras", "review"] as const;
const STEP_LABEL: Record<(typeof STEPS)[number], string> = {
  match: "request.stepMatch",
  travel: "request.stepTravel",
  rooms: "request.stepRooms",
  extras: "request.stepExtras",
  review: "request.stepReview",
};
// Fields to validate before leaving each step (empty = nothing required to advance).
const STEP_FIELDS: FieldPath<SubmitTripRequestInput>[][] = [
  ["matchOpponent", "destinationCity", "travelStartDate", "travelEndDate", "paxCount"],
  ["transportModes"],
  [],
  [],
];

const DEFAULTS: SubmitTripRequestInput = {
  sport: "Hockey",
  competition: "",
  matchOpponent: "",
  destinationCity: "",
  destinationCountry: "ES",
  travelStartDate: "",
  travelEndDate: "",
  // paxCount/playerCount/staffCount are derived from the Players & Staff pickers, which start
  // unselected — the user must choose both (see the sync effect). No pre-filled head counts.
  paxCount: 0,
  playerCount: undefined,
  staffCount: undefined,
  transportModes: { flight: false, train: false, coach: false, hireCar: false },
  baggageRequirements: { notes: "" },
  roomConfig: { single: 0, double: 0, twin: 0, triple: 0 },
  boardBasis: undefined,
  extraServices: ["meetingRoom", "projector"],
  notes: "",
  hotelPreferenceNotes: "",
};

/** Map an existing trip onto the wizard's form shape, falling back to defaults for unset columns. */
function mapTripToForm(trip: TripDto): SubmitTripRequestInput {
  const tm = trip.transportModes ?? {};
  const rc = trip.roomConfig ?? {};
  return {
    // Fold legacy free-text sports onto the catalogue Select's values: an unrecognised sport
    // shows as "no sport" ("") — the payload builder drops "" so the stored value isn't clobbered
    // unless the user actively picks a sport.
    sport: canonicalSportName(trip.sport) ?? "",
    competition: trip.competition ?? "",
    matchOpponent: trip.matchOpponent ?? "",
    destinationCity: trip.destinationCity ?? "",
    destinationCountry: trip.destinationCountry ?? DEFAULTS.destinationCountry,
    travelStartDate: trip.travelStartDate ?? "",
    travelEndDate: trip.travelEndDate ?? "",
    paxCount: trip.paxCount ?? 0,
    playerCount: trip.playerCount ?? 0,
    staffCount: trip.staffCount ?? 0,
    transportModes: {
      flight: !!tm.flight,
      train: !!tm.train,
      coach: !!tm.coach,
      hireCar: !!tm.hireCar,
    },
    baggageRequirements: { notes: trip.baggageRequirements?.notes ?? "" },
    roomConfig: {
      single: rc.single ?? 0,
      double: rc.double ?? 0,
      twin: rc.twin ?? 0,
      triple: rc.triple ?? 0,
    },
    boardBasis: trip.boardBasis ?? undefined,
    extraServices: trip.extraServices ?? [],
    notes: trip.notes ?? "",
    hotelPreferenceNotes: trip.hotelPreferenceNotes ?? "",
  };
}

export function RequestPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  // Present on /trips/:id/edit, absent on /request — this is what flips the wizard into edit mode.
  const { id: editId } = useParams<{ id: string }>();
  const [step, setStep] = useState(0);
  // null = not yet selected; the user must pick both before the request can be submitted.
  const [players, setPlayers] = useState<number | null>(null);
  const [staff, setStaff] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<TripDto | null>(null);
  // While editing, the trip is fetched and the form reset to its values before the wizard renders.
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    editId ? "loading" : "ready",
  );
  // The edited trip's status, kept so the wizard can explain what submitting does at this stage
  // (post-proposal edits notify TC to update the proposal; an Expired trip is reactivated).
  const [editStatus, setEditStatus] = useState<TripStatus | null>(null);
  // Today as ISO `YYYY-MM-DD` — travel can't start in the past, so it's the picker's floor. In edit
  // mode we drop the floor so an existing (possibly already-started) trip's dates stay valid.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const dateMin = editId ? undefined : todayIso;

  const form = useForm<SubmitTripRequestInput>({
    resolver: zodResolver(submitTripRequestSchema),
    defaultValues: DEFAULTS,
    mode: "onChange",
  });
  const { control, handleSubmit, trigger, setValue, watch, formState, reset } = form;
  const { errors, isSubmitting } = formState;

  // Edit mode: load the trip once, reset the form (and the players/staff steppers) to its values.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    void (async () => {
      try {
        const trip = await tripsApi.get(editId);
        if (cancelled) return;
        reset(mapTripToForm(trip));
        setPlayers(trip.playerCount);
        setStaff(trip.staffCount);
        setEditStatus(trip.status);
        setLoadState("ready");
      } catch {
        if (!cancelled) setLoadState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, reset]);

  // Keep the persisted pax fields in sync with the players/staff steppers: the total
  // (`paxCount`, validated) plus the breakdown the trip now stores (data-model §5).
  useEffect(() => {
    // paxCount stays unset until BOTH pickers have a value, so the schema's `min(1)` blocks
    // advancing/submitting and forces the user to choose players and staff.
    const total = players != null && staff != null ? players + staff : undefined;
    setValue("paxCount", total as number, { shouldValidate: formState.isSubmitted });
    setValue("playerCount", players ?? undefined);
    setValue("staffCount", staff ?? undefined);
  }, [players, staff, setValue, formState.isSubmitted]);

  function err(name: FieldPath<SubmitTripRequestInput>): string | undefined {
    const e = name.split(".").reduce<unknown>((acc, k) => (acc as Record<string, unknown>)?.[k], errors);
    if (!e || typeof e !== "object" || !("message" in e)) return undefined;
    if (name === "transportModes") return t("request.errTransport");
    if (name === "travelEndDate" && (e as { type?: string }).type === "custom") return t("request.errDates");
    return t("request.errRequired");
  }

  async function next() {
    const fields = STEP_FIELDS[step];
    if (fields?.length && !(await trigger(fields))) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  const onValid = async (values: SubmitTripRequestInput) => {
    setSubmitError(null);
    try {
      if (editId) {
        await tripsApi.update(editId, buildPayload(values));
        navigate(`/trips/${editId}`);
      } else {
        setCreated(await tripsApi.create(buildPayload(values)));
      }
    } catch (e) {
      setSubmitError(
        e instanceof ApiError ? e.message : t(editId ? "request.editError" : "request.submitError"),
      );
    }
  };

  if (created) return <RequestSuccess onTrip={() => navigate(`/trips/${created.id}`)} onDash={() => navigate("/")} />;

  if (loadState === "loading") {
    return (
      <div className="page">
        <div className="empty">{t("common.loading")}</div>
      </div>
    );
  }
  if (loadState === "error") {
    return (
      <div className="page">
        <div className="empty">{t("trips.loadError")}</div>
        <Button variant="soft" style={{ marginTop: 14 }} onClick={() => navigate("/trips")}>
          {t("trips.backToTrips")}
        </Button>
      </div>
    );
  }

  const stepId = STEPS[step] ?? "review";
  const pct = ((step + 1) / STEPS.length) * 100;
  // Step-0 "back" target differs by mode: cancel an edit returns to the trip; a new request bails to
  // the dashboard.
  const onStepZeroBack = () => (editId ? navigate(`/trips/${editId}`) : navigate("/"));

  return (
    <div className="page">
      <div className="page-head">
        <h1>{t(editId ? "request.editTitle" : "request.title")}</h1>
        <div className="sub">{t(editId ? "request.editSub" : "request.sub")}</div>
      </div>

      {(editStatus === "ProposalSent" || editStatus === "Accepted" || editStatus === "Expired") && (
        // Editing after TC responded means more than a quiet field change — say what submitting does
        // (amendment → TC updates the proposal; expired → the request is reactivated).
        <div className="info-banner" role="status" style={{ marginBottom: "var(--gap)" }}>
          <Icon name="alert" size={16} />
          <span>{t(editStatus === "Expired" ? "request.resubmitNotice" : "request.amendNotice")}</span>
        </div>
      )}

      <div className="wizard">
        <nav className="wiz-steps">
          {STEPS.map((s, i) => (
            <button
              key={s}
              type="button"
              className={`wiz-step${i === step ? " active" : ""}${i < step ? " done" : ""}`}
              onClick={() => i <= step && setStep(i)}
            >
              <span className="wiz-num">{i < step ? <Icon name="check" size={12} stroke={2.5} /> : i + 1}</span>
              <span className="ws-label">{t(STEP_LABEL[s])}</span>
            </button>
          ))}
        </nav>

        <div>
          <div className="wiz-bar">
            <i style={{ width: `${pct}%` }} />
          </div>
          <Card>
            <h2 className="wiz-title">{t(STEP_LABEL[stepId])}</h2>

            {stepId === "match" && (
              <div className="form-grid">
                <Controller
                  control={control}
                  name="sport"
                  render={({ field }) => (
                    <Field label={t("request.fSport")}>
                      <Select
                        value={field.value ?? ""}
                        onChange={field.onChange}
                        options={[{ value: "", label: t("request.sportNone") }, ...SPORT_OPTIONS]}
                      />
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="competition"
                  render={({ field }) => (
                    <Field label={t("request.fCompetition")}>
                      <TextInput value={field.value ?? ""} onChange={field.onChange} placeholder="Euro Hockey League" />
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="matchOpponent"
                  render={({ field }) => (
                    <Field label={t("request.fMatch")} required error={err("matchOpponent")}>
                      <TextInput value={field.value ?? ""} onChange={field.onChange} placeholder="vs HC Bloemendaal" icon="trips" />
                    </Field>
                  )}
                />
                <div />
                <Controller
                  control={control}
                  name="destinationCountry"
                  render={({ field }) => (
                    <Field label={t("request.fCountry")}>
                      <CountrySelect value={field.value ?? ""} onChange={field.onChange} locale={lang} />
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="destinationCity"
                  render={({ field }) => (
                    <Field label={t("request.fDest")} required error={err("destinationCity")}>
                      <TextInput value={field.value ?? ""} onChange={field.onChange} placeholder="Madrid" icon="building" />
                    </Field>
                  )}
                />
                <div className="dates-pax-row">
                  <Field label={t("request.fDates")} required error={err("travelStartDate") ?? err("travelEndDate")}>
                    <DateRangePicker
                      locale={lang}
                      min={dateMin}
                      placeholder={t("request.fDatesPlaceholder")}
                      clearLabel={t("request.fDatesClear")}
                      value={{ start: watch("travelStartDate") || null, end: watch("travelEndDate") || null }}
                      onChange={({ start, end }) => {
                        setValue("travelStartDate", start ?? "", { shouldValidate: formState.isSubmitted });
                        setValue("travelEndDate", end ?? "", { shouldValidate: formState.isSubmitted });
                      }}
                    />
                  </Field>
                  <div className="pax-pair">
                    <Field label={t("request.fPlayers")} required error={players == null ? err("paxCount") : undefined}>
                      <Select
                        value={players == null ? "" : String(players)}
                        onChange={(v) => setPlayers(v === "" ? null : Number(v))}
                        options={[{ value: "", label: t("request.fPaxPlaceholder") }, ...PAX_OPTIONS]}
                      />
                    </Field>
                    <Field label={t("request.fStaff")} required error={staff == null ? err("paxCount") : undefined}>
                      <Select
                        value={staff == null ? "" : String(staff)}
                        onChange={(v) => setStaff(v === "" ? null : Number(v))}
                        options={[{ value: "", label: t("request.fPaxPlaceholder") }, ...PAX_OPTIONS]}
                      />
                    </Field>
                  </div>
                </div>
              </div>
            )}

            {stepId === "travel" && (
              <div className="stack-lg">
                <Controller
                  control={control}
                  name="transportModes"
                  render={({ field }) => (
                    <Field label={t("request.fTransport")} required error={err("transportModes")} group>
                      <div className="check-grid">
                        {TRANSPORT_MODES.map((m) => (
                          <CheckCard
                            key={m.key}
                            checked={Boolean(field.value?.[m.key])}
                            onChange={(v) => field.onChange({ ...field.value, [m.key]: v })}
                            icon={m.icon}
                            label={t(m.label)}
                          />
                        ))}
                      </div>
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="baggageRequirements.notes"
                  render={({ field }) => (
                    <Field label={t("request.fBaggage")} hint={t("request.fBaggageHint")}>
                      <Textarea value={field.value ?? ""} onChange={field.onChange} placeholder={t("request.fBaggageHint")} rows={2} />
                    </Field>
                  )}
                />
              </div>
            )}

            {stepId === "rooms" && (
              <div className="stack-lg">
                <Controller
                  control={control}
                  name="roomConfig"
                  render={({ field }) => (
                    <Field
                      group
                      label={t("request.fRooms")}
                      labelAction={
                        <button
                          type="button"
                          className="link"
                          onClick={() =>
                            field.onChange({
                              single: 0,
                              // One double per staff member (staff don't share); players pair up in twins.
                              double: staff ?? 0,
                              twin: Math.ceil((players ?? 0) / 2),
                              triple: 0,
                            })
                          }
                        >
                          {t("request.autoRooms")}
                        </button>
                      }
                    >
                      <div className="check-grid">
                        {ROOM_KEYS.map((r) => (
                          <div key={r.key} className="room-row">
                            <Icon name="bed" size={18} className="check-card-icon" />
                            <span className="check-card-label">{t(r.label)}</span>
                            <span className="room-stepper">
                              <Stepper
                                value={field.value?.[r.key] ?? 0}
                                onChange={(v) => field.onChange({ ...field.value, [r.key]: v })}
                                min={0}
                                max={30}
                              />
                            </span>
                          </div>
                        ))}
                      </div>
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="boardBasis"
                  render={({ field }) => (
                    <Field label={t("request.fBoard")} group>
                      <div className="check-grid">
                        {BOARD_OPTS.map((b) => (
                          <CheckCard
                            key={b.value}
                            checked={field.value === b.value}
                            onChange={() => field.onChange(b.value)}
                            icon="utensils"
                            label={t(b.label)}
                          />
                        ))}
                      </div>
                    </Field>
                  )}
                />
              </div>
            )}

            {stepId === "extras" && (
              <div className="stack-lg">
                <Controller
                  control={control}
                  name="extraServices"
                  render={({ field }) => {
                    const selected = field.value ?? [];
                    return (
                      <Field label={t("request.fExtras")} group>
                        <div className="check-grid check-grid-3">
                          {EXTRA_OPTS.map((x) => (
                            <CheckCard
                              key={x.key}
                              checked={selected.includes(x.key)}
                              onChange={(v) =>
                                field.onChange(v ? [...selected, x.key] : selected.filter((k) => k !== x.key))
                              }
                              icon={x.icon}
                              label={t(x.label)}
                            />
                          ))}
                        </div>
                      </Field>
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="hotelPreferenceNotes"
                  render={({ field }) => (
                    <Field label={t("request.fHotelPref")} hint={t("request.fHotelHint")}>
                      <Textarea value={field.value ?? ""} onChange={field.onChange} placeholder={t("request.fHotelHint")} rows={2} />
                    </Field>
                  )}
                />
                <Controller
                  control={control}
                  name="notes"
                  render={({ field }) => (
                    <Field label={t("request.fNotes")} hint={t("request.fNotesHint")}>
                      <Textarea value={field.value ?? ""} onChange={field.onChange} placeholder={t("request.fNotesHint")} rows={3} />
                    </Field>
                  )}
                />
              </div>
            )}

            {stepId === "review" && (
              <ReviewStep form={form} players={players} staff={staff} goStep={setStep} />
            )}

            {submitError && (
              <div className="auth-alert" style={{ marginTop: 18 }} role="alert">
                <Icon name="alert" size={16} className="aa-ic" />
                <div className="aa-body">{submitError}</div>
              </div>
            )}

            <div className="wiz-foot">
              <Button variant="ghost" icon="chevL" onClick={() => (step > 0 ? setStep(step - 1) : onStepZeroBack())}>
                {step > 0 ? t("actions.back") : editId ? t("actions.cancel") : t("request.backDash")}
              </Button>
              {step < STEPS.length - 1 ? (
                <Button variant="primary" iconRight="chevR" onClick={next}>
                  {t("actions.next")}
                </Button>
              ) : (
                <Button variant="primary" icon="send" disabled={isSubmitting} onClick={handleSubmit(onValid)}>
                  {t(editId ? "request.saveChanges" : "request.submit")}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/** Build the API payload, dropping empty optionals so unset columns stay null. */
function buildPayload(v: SubmitTripRequestInput): CreateTripInput {
  const trimmed = (s?: string) => (s && s.trim() ? s.trim() : undefined);
  const payload: CreateTripInput = {
    matchOpponent: v.matchOpponent,
    destinationCity: v.destinationCity,
    travelStartDate: v.travelStartDate,
    travelEndDate: v.travelEndDate,
    paxCount: v.paxCount,
    playerCount: v.playerCount,
    staffCount: v.staffCount,
    transportModes: v.transportModes,
    boardBasis: v.boardBasis,
    sport: trimmed(v.sport),
    competition: trimmed(v.competition),
    destinationCountry: trimmed(v.destinationCountry),
    notes: trimmed(v.notes),
    hotelPreferenceNotes: trimmed(v.hotelPreferenceNotes),
  };
  const baggage = trimmed(v.baggageRequirements?.notes);
  if (baggage) payload.baggageRequirements = { notes: baggage };
  if (v.roomConfig && Object.values(v.roomConfig).some((n) => (n ?? 0) > 0)) payload.roomConfig = v.roomConfig;
  if (v.extraServices?.length) payload.extraServices = v.extraServices;
  return payload;
}

function ReviewStep({
  form,
  players,
  staff,
  goStep,
}: {
  form: ReturnType<typeof useForm<SubmitTripRequestInput>>;
  players: number | null;
  staff: number | null;
  goStep: (step: number) => void;
}) {
  const { t } = useTranslation();
  const { date, country } = useFormatters();
  const v = form.getValues();

  const transport = TRANSPORT_MODES.filter((m) => v.transportModes?.[m.key]).map((m) => t(m.label)).join(", ") || "—";
  const rooms =
    ROOM_KEYS.filter((r) => (v.roomConfig?.[r.key] ?? 0) > 0)
      .map((r) => `${v.roomConfig?.[r.key]} ${t(r.label).toLowerCase()}`)
      .join(", ") || "—";
  const extras = EXTRA_OPTS.filter((x) => v.extraServices?.includes(x.key)).map((x) => t(x.label)).join(", ") || "—";
  const board = BOARD_OPTS.find((b) => b.value === v.boardBasis);
  const destination = [v.destinationCity, v.destinationCountry ? country(v.destinationCountry) : ""]
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      <p className="muted wiz-review-intro">{t("request.reviewIntro")}</p>
      <ReviewSection title={t("request.stepMatch")} onEdit={() => goStep(0)}>
        <KV k={t("request.fSport")} v={v.sport} />
        <KV k={t("request.fCompetition")} v={v.competition} />
        <KV k={t("request.fMatch")} v={v.matchOpponent} />
        <KV k={t("request.fDest")} v={destination} />
        <KV
          k={t("request.fDates")}
          v={[v.travelStartDate && date(v.travelStartDate), v.travelEndDate && date(v.travelEndDate)].filter(Boolean).join(" – ")}
        />
        <KV
          k={t("request.fPax")}
          v={`${(players ?? 0) + (staff ?? 0)} (${players ?? 0} ${t("request.fPlayers")}, ${staff ?? 0} ${t("request.fStaff")})`}
        />
      </ReviewSection>
      <ReviewSection title={t("request.stepTravel")} onEdit={() => goStep(1)}>
        <KV k={t("request.fTransport")} v={transport} />
        <KV k={t("request.fBaggage")} v={v.baggageRequirements?.notes} />
      </ReviewSection>
      <ReviewSection title={t("request.stepRooms")} onEdit={() => goStep(2)}>
        <KV k={t("request.fRooms")} v={rooms} />
        <KV k={t("request.fBoard")} v={board ? t(board.label) : ""} />
      </ReviewSection>
      <ReviewSection title={t("request.stepExtras")} onEdit={() => goStep(3)}>
        <KV k={t("request.fExtras")} v={extras} />
        <KV k={t("request.fHotelPref")} v={v.hotelPreferenceNotes} />
        <KV k={t("request.fNotes")} v={v.notes} />
      </ReviewSection>
    </div>
  );
}

function ReviewSection({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="review-sec">
      <div className="review-head">
        <h4>{title}</h4>
        <Button variant="ghost" size="sm" icon="edit" onClick={onEdit}>
          {t("actions.edit")}
        </Button>
      </div>
      <div className="kv-grid">{children}</div>
    </div>
  );
}

function KV({ k, v }: { k: string; v?: string | null }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className="kv-v">{v || "—"}</span>
    </div>
  );
}

function RequestSuccess({ onTrip, onDash }: { onTrip: () => void; onDash: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="page">
      <div className="success-screen">
        <div className="success-ic">
          <Icon name="check" size={34} stroke={2.5} />
        </div>
        <h1>{t("request.doneTitle")}</h1>
        <p className="muted success-desc">{t("request.doneDesc")}</p>
        <div className="success-actions">
          <Button variant="secondary" onClick={onDash}>
            {t("request.backDash")}
          </Button>
          <Button variant="primary" iconRight="arrowR" onClick={onTrip}>
            {t("request.viewTrip")}
          </Button>
        </div>
      </div>
    </div>
  );
}
